import { diffWords } from 'diff';
import { useContext } from 'react';
import styled from 'styled-components';
import { StyledH5 } from '@/Shared/Typography/typography';
import CourseEditContext from './CourseEditContext';

const StyledDiffContent = styled.div`
	font-size: 15px;
	line-height: 1.65;
	color: #374151;

	ul,
	ol {
		display: block;
		margin-block-start: 0.75em;
		margin-block-end: 0.75em;
		margin-inline-start: 0;
		margin-inline-end: 0;
		padding-inline-start: 1.5rem;
		unicode-bidi: isolate;
	}

	ol {
		list-style-type: decimal;
	}

	ul {
		list-style-type: disc;
	}

	p:empty {
		min-height: 1.2em;
	}
`;

interface CourseDiffProps {
	original: string;
	draft: string;
	title?: string;
}

interface TaggedRange {
	start: number;
	end: number;
	spanHtml: string;
}

/** Strip all HTML to plain text, preserving line structure for word-level diffing. */
const stripTags = (str: string): string => {
	if (!str) return '';
	const withPlaceholders = str.replace(/<iframe[^>]*>[\s\S]*?<\/iframe>/gi, ' (VIDEO) ');
	const doc = new DOMParser().parseFromString(withPlaceholders, 'text/html');
	const blocks = doc.body.querySelectorAll('p, div, li, br, h1, h2, h3, h4, h5, h6');
	for (const el of blocks) {
		el.after(document.createTextNode('\n'));
	}
	return (doc.body.textContent ?? '').replace(/\n{3,}/g, '\n\n').trim();
};

/**
 * Build a list of character ranges in `strippedText` that correspond to
 * tagged-word spans in the raw HTML draft. Used to restore span styling in
 * the diff output without affecting the text comparison.
 */
const buildTaggedRanges = (rawHtml: string, strippedText: string): TaggedRange[] => {
	const doc = new DOMParser().parseFromString(rawHtml, 'text/html');
	const ranges: TaggedRange[] = [];
	let searchFrom = 0;
	for (const el of doc.body.querySelectorAll('[class*="tagged-word"]')) {
		const text = el.textContent ?? '';
		if (!text) continue;
		const pos = strippedText.indexOf(text, searchFrom);
		if (pos === -1) continue;
		ranges.push({ start: pos, end: pos + text.length, spanHtml: (el as HTMLElement).outerHTML });
		searchFrom = pos + text.length;
	}
	return ranges;
};

const CourseDiff = ({ original, draft, title }: CourseDiffProps) => {
	const { isApprovalMode } = useContext(CourseEditContext);

	if (!isApprovalMode) return null;

	const sanitizedOriginal = stripTags(original);
	const sanitizedDraft = stripTags(draft);

	const isEmpty = (s: string) =>
		(s ?? '')
			.replace(/ style="([^"]*)"/g, '')
			.replaceAll(/<[^>]*>/gm, '')
			.trim() === '';

	const unchanged = original === draft;

	const toHtml = (text: string) => text.replace(/\n/g, '<br>');

	let diffHtml = '';
	if (!unchanged) {
		const taggedRanges = buildTaggedRanges(draft, sanitizedDraft);
		let draftCursor = 0;

		for (const part of diffWords(sanitizedOriginal, sanitizedDraft)) {
			const partLen = part.value.length;

			if (part.removed) {
				const val = toHtml(part.value);
				if (part.value.includes('(VIDEO)')) {
					diffHtml += `<span style="box-shadow:0 0 0 3px #fee2e2;display:inline-block;line-height:0;text-decoration:line-through;">${val}</span>`;
				} else {
					diffHtml += `<span style="background:#fee2e2;color:#991b1b;text-decoration:line-through;">${val}</span>`;
				}
				continue;
			}

			const partEnd = draftCursor + partLen;

			// Find tagged ranges that start inside this part
			const inside = taggedRanges.filter((r) => r.start >= draftCursor && r.start < partEnd);

			if (inside.length === 0) {
				// No tagged words — render the whole part uniformly
				const val = toHtml(part.value);
				if (part.added) {
					diffHtml += part.value.includes('(VIDEO)')
						? `<span style="box-shadow:0 0 0 3px #d1fae5;display:inline-block;line-height:0;">${val}</span>`
						: `<span style="background:#d1fae5;color:#065f46;">${val}</span>`;
				} else {
					diffHtml += `<span style="color:#374151;">${val}</span>`;
				}
			} else {
				// Split the part into segments around tagged-word spans
				let pos = draftCursor;
				for (const range of inside) {
					// Text before this tagged word
					if (pos < range.start) {
						const before = part.value.slice(pos - draftCursor, range.start - draftCursor);
						if (before) {
							diffHtml += part.added
								? `<span style="background:#d1fae5;color:#065f46;">${toHtml(before)}</span>`
								: `<span style="color:#374151;">${toHtml(before)}</span>`;
						}
					}
					// The tagged word span itself
					diffHtml += part.added
						? `<span style="background:#d1fae5;">${range.spanHtml}</span>`
						: range.spanHtml;
					pos = range.end;
				}
				// Text after the last tagged word (still within this part)
				if (pos < partEnd) {
					const after = part.value.slice(pos - draftCursor);
					if (after) {
						diffHtml += part.added
							? `<span style="background:#d1fae5;color:#065f46;">${toHtml(after)}</span>`
							: `<span style="color:#374151;">${toHtml(after)}</span>`;
					}
				}
			}

			draftCursor += partLen;
		}
	}

	const originalHtml = isEmpty(sanitizedOriginal)
		? '<span style="color:#9ca3af">—</span>'
		: original;

	const proposedHtml = unchanged
		? '<span style="color:#9ca3af;font-style:italic;font-size:14px;">No changes</span>'
		: diffHtml || '<span style="color:#9ca3af">—</span>';

	return (
		<div className="mb-5">
			{title && <StyledH5 mb="8px">{title}</StyledH5>}
			<div className="grid grid-cols-2 rounded-md border border-gray-200 overflow-hidden">
				<div className="px-3 py-1.5 text-[13px] font-semibold uppercase tracking-wide text-gray-400 bg-gray-50 border-b border-r border-gray-200">
					Current Version
				</div>
				<div className="px-3 py-1.5 text-[13px] font-semibold uppercase tracking-wide text-gray-400 bg-gray-50 border-b border-gray-200">
					Proposed Changes
				</div>
				<StyledDiffContent
					className="px-3 py-3 border-r border-gray-200 bg-gray-50/40"
					// biome-ignore lint/security/noDangerouslySetInnerHtml: controlled HTML from rich-text editor
					dangerouslySetInnerHTML={{ __html: originalHtml }}
				/>
				<StyledDiffContent
					className="px-3 py-3"
					// biome-ignore lint/security/noDangerouslySetInnerHtml: controlled diff HTML
					dangerouslySetInnerHTML={{ __html: proposedHtml }}
				/>
			</div>
		</div>
	);
};

export default CourseDiff;
